import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import TemplateSchedulingService from '../../_services/TemplateSchedulingService';
import ConflictReport from './components/ConflictReport';
import './styles.scss';

interface Template {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  activitySlotType: number;
  activeStatus: number;
  yearName: string;
  programName: string;
  courseName: string;
}

interface TimetablePermissions {
  canTakeAttendance: boolean;
  canEditTemplate: boolean;
  canDeleteTemplate: boolean;
  canCreateTemplate: boolean;
}

const SchedulesTemplates: React.FC = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<TimetablePermissions>({
    canTakeAttendance: true,
    canEditTemplate: true,
    canDeleteTemplate: true,
    canCreateTemplate: true,
  });
  const [filters, setFilters] = useState({
    name: '',
    yearId: '',
    programId: '',
    courseId: '',
    activeStatus: '1', // Default to active only
    startDate: '',
    endDate: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalPages: 0,
    totalCount: 0,
  });
  const [sort, setSort] = useState({ sortBy: 'id', sortOrder: 'desc' });

  // Dropdown options
  const [years, setYears] = useState<{ id: number; name: string }[]>([]);
  const [programs, setPrograms] = useState<{ id: number; name: string }[]>([]);
  const [courses, setCourses] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [pagination.page, sort.sortBy, sort.sortOrder]);

  const fetchDropdownData = async () => {
    try {
      const [yearsRes, programsRes, coursesRes] = await Promise.all([
        ApiService.getYearMastersForTemplates(),
        ApiService.getPrograms(),
        ApiService.getCourses(),
      ]);
      if (yearsRes.data?.data) setYears(yearsRes.data.data.map((y: any) => ({ id: y.yearId, name: y.yearName })));
      if (programsRes.data?.data) setPrograms(programsRes.data.data);
      if (coursesRes.data?.data) setCourses(coursesRes.data.data);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await ApiService.getTemplates(filters, pagination, sort);
      if (response.data.success) {
        setTemplates(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
        }));
        if (response.data.permissions) {
          setPermissions(response.data.permissions);
        }
      } else {
        setLoadError(response.data?.message || 'Failed to load templates.');
      }
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      const detail = error?.response?.data?.message || error?.message || 'Backend unreachable.';
      setLoadError(`Failed to load templates: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTemplates();
  };

  const handleClearFilters = () => {
    setFilters({
      name: '',
      yearId: '',
      programId: '',
      courseId: '',
      activeStatus: '1',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (templateId: number, templateName: string) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateName}"?`)) {
      return;
    }

    try {
      const response = await ApiService.deleteTemplate(templateId);
      if (response.data.success) {
        alert('Template deleted successfully');
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Error deleting template:', error);
      alert(error.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleSort = (column: string) => {
    setSort(prev => ({
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIndicator = (column: string) => {
    if (sort.sortBy === column) {
      return <span className="sort-indicator active">{sort.sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>;
    }
    return <span className="sort-indicator"> ⇅</span>;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // dd/MM/yyyy format
  };

  return (
    <div className="schedules-templates-page">
      <div className="page-header">
        <h1>Schedule Templates & Resources</h1>
        <button
          className="btn btn-timecraft"
          onClick={() => history.push('/timecraft-input-management')}
          title="Manage TimeCraft inputs & generate timetables"
        >
          🗓️ Timecraft
        </button>
      </div>

      {loadError && (
        <div
          role="alert"
          style={{
            margin: '12px 0',
            padding: '10px 14px',
            borderRadius: 6,
            background: '#fff5f5',
            border: '1px solid #ffcdd2',
            color: '#c62828',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            fontSize: 13,
          }}
        >
          <span>⚠ {loadError}</span>
          <button
            onClick={() => fetchTemplates()}
            style={{
              background: '#c62828',
              color: 'var(--text-on-brand)',
              border: 'none',
              borderRadius: 4,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="stt-tabs-bar">
        <button
          className={`stt-tab-btn${activeTab === 0 ? ' active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          Timetable Templates
        </button>
        <button
          className={`stt-tab-btn${activeTab === 2 ? ' active' : ''}`}
          onClick={() => setActiveTab(2)}
        >
          Conflict Report
        </button>
        {activeTab === 0 && permissions.canCreateTemplate && (
          <button
            className="btn btn-primary stt-create-btn"
            onClick={() => history.push('/schedules-templates/new')}
          >
            + Create New Template
          </button>
        )}
      </div>

      {activeTab === 2 ? (
        <ConflictReport templates={templates.map(t => ({ id: t.id, name: t.name }))} />
      ) : (
      <>
      {/* Filters Section */}
      <div className="stt-filters-panel">
        <div className="filter-grid">
          <div className="filter-field">
            <label>Template Name</label>
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            />
          </div>

          <div className="filter-field">
            <label>Status</label>
            <select
              value={filters.activeStatus}
              onChange={(e) => setFilters({ ...filters, activeStatus: e.target.value })}
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div className="filter-field">
            <label>Academic Year</label>
            <select
              value={filters.yearId}
              onChange={(e) => setFilters({ ...filters, yearId: e.target.value })}
            >
              <option value="">All Years</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>{y.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-field">
            <label>Program</label>
            <select
              value={filters.programId}
              onChange={(e) => setFilters({ ...filters, programId: e.target.value })}
            >
              <option value="">All Programs</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-field">
            <label>Course</label>
            <select
              value={filters.courseId}
              onChange={(e) => setFilters({ ...filters, courseId: e.target.value })}
            >
              <option value="">All Courses</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-field">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>

          <div className="filter-field">
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn btn-search" onClick={handleSearch}>
            Search
          </button>
          <button className="btn btn-clear" onClick={handleClearFilters}>
            Clear
          </button>
        </div>
      </div>

      {/* Results Table */}
      <div className="templates-table-container">
        {loading ? (
          <div className="loading">Loading templates...</div>
        ) : (
          <table className="templates-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('id')}>ID{sortIndicator('id')}</th>
                <th className="sortable" onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                <th className="sortable" onClick={() => handleSort('startDate')}>Start Dt{sortIndicator('startDate')}</th>
                <th className="sortable" onClick={() => handleSort('endDate')}>End Dt{sortIndicator('endDate')}</th>
                <th className="sortable" onClick={() => handleSort('yearName')}>Year{sortIndicator('yearName')}</th>
                <th>Program/Course</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                {(permissions.canEditTemplate || permissions.canDeleteTemplate) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={(permissions.canEditTemplate || permissions.canDeleteTemplate) ? 8 : 7} className="no-data">
                    No templates found
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id} onClick={() => history.push(`/schedules-templates/${template.id}`)}>
                    <td>{template.id}</td>
                    <td>{template.name}</td>
                    <td>{formatDate(template.startDate)}</td>
                    <td>{formatDate(template.endDate)}</td>
                    <td>{template.yearName || '-'}</td>
                    <td>{template.programName || template.courseName || '-'}</td>
                    <td>
                      <span className={`status-badge ${template.activeStatus === 1 ? 'active' : 'inactive'}`}>
                        {template.activeStatus === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEditTemplate || permissions.canDeleteTemplate) && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          {permissions.canEditTemplate && (
                            <button
                              className="btn-edit"
                              onClick={() => history.push(`/schedules-templates/${template.id}?edit=true`)}
                            >
                              Edit
                            </button>
                          )}
                          {permissions.canEditTemplate && (
                            <button
                              className="btn-schedule"
                              onClick={() => history.push(`/template-scheduling-board/${template.id}`)}
                              title="Open in Scheduling Board"
                            >
                              Schedule
                            </button>
                          )}
                          {permissions.canEditTemplate && (
                            <button
                              className="btn-schedule"
                              onClick={async () => {
                                try {
                                  const res: any = await TemplateSchedulingService.copyTemplate(template.id);
                                  if (res.data.success) {
                                    alert(`Copied: "${res.data.data.name}" with ${res.data.data.slotsCopied} slots`);
                                    window.location.reload();
                                  }
                                } catch (err: any) {
                                  alert('Failed to copy: ' + (err.response?.data?.message || err.message));
                                }
                              }}
                              title="Duplicate this template with all slots"
                            >
                              Copy
                            </button>
                          )}
                          {permissions.canDeleteTemplate && (
                            <button
                              className="btn-delete"
                              onClick={() => handleDelete(template.id, template.name)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination & Total Count */}
        {!loading && (
          <div className="pagination">
            <span className="total-count">
              {pagination.totalCount} timetable{pagination.totalCount !== 1 ? 's' : ''} found
            </span>
            {pagination.totalPages > 1 && (
              <>
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </button>
              </>
            )}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default SchedulesTemplates;
